import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { adminUserService } from 'pricing/services';

interface IAdminUserActions {
  selectionCount?: number;
  position?: NotificationPosition;
}

export const useAdminUserActions = ({ selectionCount, position }: IAdminUserActions = {}) => {
  return {
    ...useUpsertAdminUser(),
    ...useRemoveAdminUsers(selectionCount, position),
  };
};

const useUpsertAdminUser = () => {
  const { errors } = useHandleErrors({
    success: ({ pricingProfileCorporateUser }) =>
      pricingProfileCorporateUser?.id
        ? { message: t('messageAdminUserUpdateSuccess') }
        : { message: t('messageAdminUserCreateSuccess') },
    loading: ({ pricingProfileCorporateUser }) =>
      pricingProfileCorporateUser?.id
        ? { message: t('messageAdminUserUpdateInProgress') }
        : { message: t('messageAdminUserCreateInProgress') },
    error: ({ pricingProfileCorporateUser }) =>
      pricingProfileCorporateUser?.id
        ? { title: t('errorTitleAdminUserUpdate') }
        : { title: t('errorTitleAdminUserCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (data) => adminUserService.upsertPricingProfileAdminUser(data),
    errors
  );

  return { upsertAdminUser: mutateAsync };
};

const useRemoveAdminUsers = (
  selectionCount: number,
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageAdminUserRemoveSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageAdminUserRemoveInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitleAdminUserRemove', { count: selectionCount }),
    },
    position,
  });

  const { mutateAsync } = useMutation(
    (data) => adminUserService.removePricingProfileAdminUsers(data),
    errors
  );

  return { removeAdminUsers: mutateAsync };
};
